const OWN_BRANDS = ['nin', 'zeronovo', 'vitanovo'];

export const normalizeProductText = (value = '') => String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

export const getProductVariants = (product) => product.prod_variants || [];

export const getVariantStock = (variant) => {
    const stock = Number(variant.stock);
    return Number.isFinite(stock) ? stock : 0;
};

export const getProductStock = (product) => getProductVariants(product)
    .reduce((total, variant) => total + getVariantStock(variant), 0);

export const getVariantPrice = (variant) => {
    const price = Number(variant.price);
    return Number.isFinite(price) ? price : 0;
};

export const getProductPriceRange = (product) => {
    const prices = getProductVariants(product).map(getVariantPrice);

    if (prices.length === 0)
        return { min: 0, max: 0 };

    return {
        min: Math.min(...prices),
        max: Math.max(...prices)
    };
};

export const getInventoryStatus = (stock) => {
    if (stock <= 0)
        return 'out';
    if (stock <= 5)
        return 'low';
    return 'available';
};

export const getProductCategories = (product) => {
    if (product.prod_category?.length > 0)
        return product.prod_category.map(({ category }) => category).filter(Boolean);

    return product.category || [];
};

export const isOwnBrand = (brand) => OWN_BRANDS.includes(
    normalizeProductText(brand)
);

export const getProductSection = (product) => {
    if (product.catalog_status === 'archived')
        return 'archived';
    if (product.medicine === true)
        return 'medicines';
    if (product.refrigerated === true)
        return 'refrigerated';
    if (isOwnBrand(product.brand))
        return 'ownBrands';
    return 'thirdParty';
};

export const shouldShowRefrigeratedMedicineNotice = (product, sectionKey) => (
    sectionKey === 'medicines'
    && product.medicine === true
    && product.refrigerated === true
);

const matchesSearch = (product, search) => {
    if (!search)
        return true;

    const productFields = [product.title, product.brand, ...getProductCategories(product)];
    const variantFields = getProductVariants(product).map(
        ({ variant_title }) => variant_title
    );

    return [...productFields, ...variantFields].some(value => normalizeProductText(value)
        .includes(search));
};

const matchesSelectedValues = (values, selectedValues) => {
    if (selectedValues.length === 0)
        return true;

    return values.some(value => selectedValues.includes(normalizeProductText(value)));
};

const matchesVariantFilters = (product, filters) => {
    const variants = getProductVariants(product);
    const minPrice = filters.minPrice === '' ? null : Number(filters.minPrice);
    const maxPrice = filters.maxPrice === '' ? null : Number(filters.maxPrice);

    if (minPrice !== null && (!Number.isFinite(minPrice) || minPrice < 0))
        return false;
    if (maxPrice !== null && (!Number.isFinite(maxPrice) || maxPrice < 0))
        return false;

    return variants.some(variant => {
        const price = getVariantPrice(variant);
        const stock = getVariantStock(variant);
        const matchesPrice = (minPrice === null || price >= minPrice)
            && (maxPrice === null || price <= maxPrice);
        const matchesInventory = filters.inventory === 'all'
            || getInventoryStatus(stock) === filters.inventory;

        return matchesPrice && matchesInventory;
    });
};

export const filterProducts = (products, filters) => products.filter(product => {
    const normalizedSearch = normalizeProductText(filters.search);
    const selectedBrands = filters.brands.map(normalizeProductText);
    const selectedCategories = filters.categories.map(normalizeProductText);

    return matchesSearch(product, normalizedSearch)
        && matchesSelectedValues([product.brand], selectedBrands)
        && matchesSelectedValues(getProductCategories(product), selectedCategories)
        && matchesVariantFilters(product, filters);
});

export const sortProducts = (products, sort) => [...products].sort((first, second) => {
    const firstName = normalizeProductText(first.title);
    const secondName = normalizeProductText(second.title);
    const firstPrices = getProductPriceRange(first);
    const secondPrices = getProductPriceRange(second);
    let result = 0;

    if (sort === 'name-desc')
        result = secondName.localeCompare(firstName, 'es');
    else if (sort === 'stock-asc')
        result = getProductStock(first) - getProductStock(second);
    else if (sort === 'stock-desc')
        result = getProductStock(second) - getProductStock(first);
    else if (sort === 'price-asc')
        result = firstPrices.min - secondPrices.min;
    else if (sort === 'price-desc')
        result = secondPrices.max - firstPrices.max;
    else
        result = firstName.localeCompare(secondName, 'es');

    return result || firstName.localeCompare(secondName, 'es');
});

export const groupProductsBySection = (products) => products.reduce((sections, product) => {
    sections[getProductSection(product)].push(product);
    return sections;
}, {
    ownBrands: [],
    thirdParty: [],
    medicines: [],
    refrigerated: [],
    archived: []
});

export const getProductFilterOptions = (products) => ({
    brands: [...new Set(products.map(({ brand }) => brand).filter(Boolean))]
        .sort((first, second) => first.localeCompare(second, 'es')),
    categories: [...new Set(products.flatMap(getProductCategories).filter(Boolean))]
        .sort((first, second) => first.localeCompare(second, 'es'))
});
